import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { api, AED } from '../lib/api';
import { Loader2, Sparkles, ArrowLeft, Plus, Trash2 } from 'lucide-react';
import AIRefiner from '../components/AIRefiner';

export default function BOQCompare() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(null);
  const [refineTarget, setRefineTarget] = useState(null); // boqId currently being refined
  const [qaHistory, setQaHistory] = useState({}); // boqId -> [{q,a}]

  const load = () => api.get(`/boqs/compare/${id}`).then(r => setData(r.data));
  useEffect(() => { load(); }, [id]);

  const runAudit = async (boqId) => {
    setBusy(boqId);
    try {
      await api.post(`/boqs/${boqId}/audit`, {});
      setQaHistory({ ...qaHistory, [boqId]: [] });
      await load();
    } finally { setBusy(null); }
  };

  const refineAudit = async (boqId, newPairs) => {
    setRefineTarget(boqId);
    const combined = [...(qaHistory[boqId] || []), ...newPairs];
    try {
      await api.post(`/boqs/${boqId}/audit`, { qa_pairs: combined });
      setQaHistory({ ...qaHistory, [boqId]: combined });
      await load();
    } finally { setRefineTarget(null); }
  };

  if (!data) return <div className="p-10">Loading…</div>;

  // Build category-grouped comparison table
  const categories = [...new Set(data.lines.map(l => l.category))];
  const vendorsList = data.boqs;

  return (
    <div className="py-6 max-w-[1400px] mx-auto space-y-6" data-testid="boq-compare-page">
      <Link to={`/app/properties/${id}`} className="text-sm text-slate-500 hover:text-slate-800 flex items-center gap-1"><ArrowLeft size={14}/> Back to property</Link>
      <div className="flex items-center justify-between">
        <div>
          <div className="overline">BOQ Intelligence</div>
          <h1 className="font-serif text-4xl">Vendor comparison & fraud check</h1>
        </div>
        <NewBOQButton propertyId={id} onCreated={load}/>
      </div>

      {/* Summary cards per vendor */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {vendorsList.map(b => (
          <div key={b.id} className="glass p-5">
            <div className="flex items-start justify-between">
              <div>
                <div className="font-medium text-sm">{b.title}</div>
                <div className="text-xs text-slate-500 mt-1">{data.vendors[b.vendor_id]?.company}</div>
                <div className="mt-3 font-serif text-3xl">{AED(b.total_aed)}</div>
              </div>
              <div className="text-right">
                {b.ai_fraud_score != null && (
                  <div className={`w-16 h-16 rounded-full grid place-items-center font-serif text-lg ${b.ai_fraud_score >= 60 ? 'bg-red-100 text-red-700' : b.ai_fraud_score >= 30 ? 'bg-amber-100 text-amber-700' : 'bg-emerald-100 text-emerald-700'}`}>
                    {Math.round(b.ai_fraud_score)}
                  </div>
                )}
                <div className="overline text-[0.6rem] mt-1">Fraud score</div>
              </div>
            </div>
            {b.ai_flags?.length > 0 && (
              <ul className="mt-4 text-xs text-slate-600 space-y-1 list-disc pl-4">
                {b.ai_flags.slice(0, 5).map((f, i) => <li key={i}>{f}</li>)}
              </ul>
            )}
            <button onClick={() => runAudit(b.id)} disabled={busy === b.id}
                    data-testid={`audit-boq-${b.id}`}
                    className="btn-gold text-xs mt-4 flex items-center gap-2">
              {busy === b.id ? <><Loader2 size={12} className="animate-spin"/> Auditing…</> : <><Sparkles size={12}/> {b.ai_fraud_score != null ? 'Re-audit' : 'Run AI audit'}</>}
            </button>

            {b.ai_clarifying_questions?.length > 0 && (
              <div className="mt-4">
                <AIRefiner
                  questions={b.ai_clarifying_questions}
                  onRefine={(pairs) => refineAudit(b.id, pairs)}
                  refining={refineTarget === b.id}
                  refinedCount={(qaHistory[b.id] || []).length > 0 ? 1 : 0}
                  compact
                />
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Comparison table */}
      <div className="glass p-6 overflow-x-auto">
        <div className="font-serif text-2xl mb-4">Line-by-line comparison</div>
        <table className="w-full text-sm">
          <thead>
            <tr className="overline text-left border-b border-slate-200/60">
              <th className="py-2">Category</th>
              <th>Description</th>
              {vendorsList.map(v => <th key={v.id} className="text-right">{data.vendors[v.vendor_id]?.company?.slice(0,20)}</th>)}
            </tr>
          </thead>
          <tbody>
            {categories.map(cat => {
              const lines = data.lines.filter(l => l.category === cat);
              const descs = [...new Set(lines.map(l => l.description))];
              return descs.map((desc, di) => {
                const rowCells = vendorsList.map(v => {
                  const line = lines.find(l => l.description === desc && l.vendor_id === v.vendor_id);
                  return line;
                });
                const totals = rowCells.filter(Boolean).map(l => l.total_aed);
                const min = totals.length > 0 ? Math.min(...totals) : null;
                return (
                  <tr key={cat + di} className="border-b border-slate-100/70">
                    <td className="py-2 text-xs overline">{di === 0 ? cat : ''}</td>
                    <td className="text-xs max-w-sm">{desc}</td>
                    {rowCells.map((line, i) => (
                      <td key={i} className={`text-right text-xs ${line?.total_aed === min && totals.length > 1 ? 'text-emerald-700 font-semibold' : ''} ${line?.ai_flag ? 'text-red-600' : ''}`}>
                        {line ? (
                          <div>
                            <div>{AED(line.total_aed)}</div>
                            <div className="text-[0.65rem] text-slate-400">{line.quantity} {line.unit} × {line.unit_rate_aed}</div>
                            {line.ai_flag && <div className="overline text-[0.55rem] text-red-500 mt-0.5">⚠ {line.ai_flag.replace('_',' ')}</div>}
                          </div>
                        ) : <span className="text-slate-300">—</span>}
                      </td>
                    ))}
                  </tr>
                );
              });
            })}
            <tr className="font-semibold border-t-2 border-slate-300">
              <td className="py-3" colSpan={2}>Total</td>
              {vendorsList.map(v => <td key={v.id} className="text-right">{AED(v.total_aed)}</td>)}
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}

function NewBOQButton({ propertyId, onCreated }) {
  const [open, setOpen] = useState(false);
  const [vendors, setVendors] = useState([]);
  const [form, setForm] = useState({ vendor_id: '', title: '', lines: [] });
  const [newLine, setNewLine] = useState({ category: '', description: '', unit: 'lot', quantity: 1, unit_rate_aed: 0 });
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (open) api.get('/vendors').then(r => setVendors(r.data)); }, [open]);

  const addLine = () => {
    if (!newLine.description) return;
    setForm({ ...form, lines: [...form.lines, { ...newLine, total_aed: newLine.quantity * newLine.unit_rate_aed }] });
    setNewLine({ category: newLine.category, description: '', unit: 'lot', quantity: 1, unit_rate_aed: 0 });
  };
  const rmLine = (i) => setForm({ ...form, lines: form.lines.filter((_, idx) => idx !== i) });

  const submit = async () => {
    setBusy(true);
    try {
      await api.post('/boqs', { property_id: propertyId, vendor_id: form.vendor_id, title: form.title, lines: form.lines });
      setOpen(false); setForm({ vendor_id: '', title: '', lines: [] });
      onCreated();
    } finally { setBusy(false); }
  };

  const total = form.lines.reduce((s, l) => s + (l.quantity * l.unit_rate_aed), 0);

  return (
    <>
      <button onClick={()=>setOpen(true)} data-testid="new-boq-btn" className="btn-primary text-sm flex items-center gap-2"><Plus size={14}/> New BOQ</button>
      {open && (
        <div className="fixed inset-0 z-40 grid place-items-center p-4 bg-slate-900/30 backdrop-blur-sm" onClick={()=>setOpen(false)}>
          <div onClick={(e)=>e.stopPropagation()} className="glass-strong w-full max-w-3xl p-6 max-h-[90vh] overflow-y-auto" data-testid="new-boq-modal">
            <div className="font-serif text-2xl mb-4">New BOQ</div>
            <div className="grid grid-cols-2 gap-3 mb-4">
              <label className="text-sm">Vendor
                <select required value={form.vendor_id} onChange={(e)=>setForm({...form, vendor_id:e.target.value})}
                        data-testid="boq-vendor"
                        className="w-full mt-1 px-3 py-2 rounded-xl border border-slate-200 bg-white/80">
                  <option value="">Choose vendor…</option>
                  {vendors.map(v => <option key={v.id} value={v.id}>{v.company}</option>)}
                </select>
              </label>
              <label className="text-sm">Title
                <input required value={form.title} onChange={(e)=>setForm({...form, title:e.target.value})}
                       data-testid="boq-title"
                       placeholder="e.g. Refurb BOQ - Marina Contracting"
                       className="w-full mt-1 px-3 py-2 rounded-xl border border-slate-200 bg-white/80"/>
              </label>
            </div>

            <div className="overline mb-2">Line items ({form.lines.length})</div>
            <div className="space-y-1 max-h-56 overflow-y-auto mb-3">
              {form.lines.map((l, i) => (
                <div key={i} className="text-sm p-2 bg-white/70 rounded-xl flex items-center gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="text-xs overline">{l.category}</div>
                    <div className="truncate">{l.description}</div>
                  </div>
                  <div className="text-xs text-right">{l.quantity} {l.unit} × {l.unit_rate_aed} = <b>{(l.quantity * l.unit_rate_aed).toLocaleString()}</b></div>
                  <button onClick={()=>rmLine(i)} className="text-slate-400 hover:text-red-500"><Trash2 size={12}/></button>
                </div>
              ))}
            </div>

            <div className="p-3 bg-white/50 rounded-2xl border border-white/70 grid grid-cols-6 gap-2 mb-3 text-xs">
              <input placeholder="Category" value={newLine.category} onChange={(e)=>setNewLine({...newLine, category:e.target.value})} className="px-2 py-1.5 rounded-lg border border-slate-200 bg-white"/>
              <input placeholder="Description" value={newLine.description} onChange={(e)=>setNewLine({...newLine, description:e.target.value})} className="col-span-2 px-2 py-1.5 rounded-lg border border-slate-200 bg-white"/>
              <input placeholder="Unit" value={newLine.unit} onChange={(e)=>setNewLine({...newLine, unit:e.target.value})} className="px-2 py-1.5 rounded-lg border border-slate-200 bg-white"/>
              <input type="number" placeholder="Qty" value={newLine.quantity} onChange={(e)=>setNewLine({...newLine, quantity:+e.target.value})} className="px-2 py-1.5 rounded-lg border border-slate-200 bg-white"/>
              <input type="number" placeholder="Rate AED" value={newLine.unit_rate_aed} onChange={(e)=>setNewLine({...newLine, unit_rate_aed:+e.target.value})} className="px-2 py-1.5 rounded-lg border border-slate-200 bg-white"/>
            </div>
            <button onClick={addLine} data-testid="boq-add-line" className="btn-ghost text-xs w-full mb-4">+ Add line</button>

            <div className="flex justify-between items-center pt-3 border-t border-slate-200/60">
              <div className="text-sm">Total: <span className="font-serif text-2xl">AED {total.toLocaleString()}</span></div>
              <div className="flex gap-2">
                <button onClick={()=>setOpen(false)} className="btn-ghost text-sm">Cancel</button>
                <button onClick={submit} disabled={busy || !form.vendor_id || !form.title || form.lines.length === 0} data-testid="boq-submit" className="btn-primary text-sm">
                  {busy ? 'Saving…' : 'Save BOQ'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
